/**
 * fp-cart-xsell.js v2 — "Customers also bought" cross-sell + length-picker
 * sheet in the cart drawer. LOCKED DESIGN (2026-07-12, "1a"/"2a").
 *
 * v1 → v2 (visual/UX only — data mechanics unchanged):
 *  · Eyebrow copy: "Pairs well with" → "Customers also bought".
 *  · Thumbs are 1:1 (96×96); taller shots crop from the top so the face
 *    stays in frame (2026-10-24; was center cover).
 *  · The ADD button is gone. A 28px "+" chip overlays the thumb's top-right;
 *    the + AND the whole image open a bottom LENGTH PICKER sheet inside the
 *    drawer dialog (top-layer safe) instead of silently adding 1.
 *  · Sheet: product row (48×48 thumb · title · ₹/mtr), −/N mts/+ stepper
 *    (38px targets, default 2, max 20), live "N mts × ₹649" calc, and
 *    "ADD TO BAG · ₹1,298" which POSTs /cart/add.js with that quantity.
 *  · Titles remain plain links to the PDP for accessibility.
 *
 * UNCHANGED from v1 (do not regress):
 *  · Recommendations: Shopify intent=related for the NEWEST in-cart product
 *    (42% of multi-item orders are the same cut in more colours).
 *  · Threshold-filler sort: within ₹600 of the next reward rung → cheap first
 *    (reads data-next-milestone off .fp-freeship-wrapper).
 *  · Anchor protocol: server renders EMPTY <div data-fp-xsell> every morph;
 *    we fill it and re-fill from cache via the drawer MutationObserver.
 *  · Silent add: POST with section ids, dispatch bare 'cart:update' (never
 *    CartAddEvent), auto-open attribute stripped for the dispatch tick.
 *  · Staleness guard on out-of-order reco fetches; one impression per cart
 *    composition, only when actually visible; fpTrack instrumentation.
 *
 * Additive + isolated: removing this script and the anchor removes the
 * feature entirely. No theme file behavior changes.
 */
(function () {
  'use strict';
  if (window.__fpXsell) return;
  window.__fpXsell = true;

  var DEBUG = function () { return window.__fpXsellDebug === true; };
  var log = function () { if (DEBUG()) { try { console.log.apply(console, ['[fp-xsell]'].concat([].slice.call(arguments))); } catch (e) {} } };

  var MAX_CARDS = 4;
  var FILLER_WINDOW_PAISE = 60000; // within ₹600 of a reward rung → cheap-first
  var QTY_DEFAULT = 1; // founder QC 2026-07-13: sheet must open at 1 like the PDP, not 2
  var QTY_MAX = 20;
  var state = {
    cartKey: '',
    sourceProductId: 0,
    products: [],
    impressionKey: '',
    inCartIds: {},
    cartTotal: 0,
    nextMilestonePaise: 0,
    recosFor: 0,
    sheet: { item: null, qty: QTY_DEFAULT, variant: null, open: false },
  };
  var refreshTimer = null;

  function track(name, props) {
    try { if (typeof window.fpTrack === 'function') window.fpTrack(name, props || {}); } catch (e) {}
  }

  // Upsell → AOV attribution. Sets an ORDER-LEVEL cart attribute `_fp_upsell`
  // that a Shopify Flow tags on ("Order created" → if attribute present → tag
  // `upsell-influenced`), so influenced orders' AOV can be compared to the rest.
  // GoKwik reads the standard Shopify cart, so cart attributes SHOULD carry to
  // the order's note_attributes — VERIFY with one live test order (GA4
  // `fp_xsell_add`+`fp_value` is the always-on fallback if they don't).
  //
  // Deliberately a cart attribute, NOT a line-item property: a per-line property
  // changes the line's merge key, so the same variant added via the upsell and
  // again via its PDP would NOT merge — splitting one continuous fabric length
  // into two cut pieces (breaks the "in one piece" promise). The cart attribute
  // never touches line items, so it's money-path-safe.
  //
  // Semantics: it means "the upsell block contributed >=1 add to this cart". It
  // is STICKY — it stays 'true' even if the shopper later removes the upsell
  // line (Shopify never auto-clears attributes, and with no line marker we can't
  // detect removal). That biases the influenced cohort's AOV toward baseline
  // (CONSERVATIVE — understates any real lift), which is the safe direction.
  // Note: unlike a line property, the leading "_" does NOT hide a cart
  // attribute — it is visible in the order's Additional details in admin (fine).
  //
  // Fire-and-forget AFTER the add resolves and WITHOUT `sections`, so it never
  // blocks the add's critical path and never triggers a second drawer morph.
  function markUpsellCart() {
    try {
      var url = (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update.js';
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: { _fp_upsell: 'true' } }),
        keepalive: true,
      }).catch(function () {});
    } catch (e) { log('mark upsell attr', e); }
  }

  function money(paise) {
    var r = paise / 100;
    var opts = r % 1 === 0 ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    try { return '₹' + r.toLocaleString('en-IN', opts); } catch (e) { return '₹' + r; }
  }

  function imgUrl(url, width) {
    if (!url) return '';
    var u = url.indexOf('//') === 0 ? 'https:' + url : url;
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // --- styles (locked pixel spec) --------------------------------------------

  function ensureStyles() {
    if (document.getElementById('fp-xsell-styles')) return;
    var s = document.createElement('style');
    s.id = 'fp-xsell-styles';
    s.textContent =
      '.fp-xsell{margin:4px 0 8px;}' +
      '.fp-xsell-eyebrow{font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:#8C8279;' +
      'font-weight:400;margin:0 0 10px;font-family:var(--font-body--family,"Inter",sans-serif);}' +
      '.fp-xsell-row{display:flex;gap:10px;overflow-x:auto;-webkit-overflow-scrolling:touch;' +
      'scrollbar-width:none;padding-bottom:2px;}' +
      '.fp-xsell-row::-webkit-scrollbar{display:none;}' +
      '.fp-xsell-card{flex:0 0 96px;min-width:0;font-family:var(--font-body--family,"Inter",sans-serif);}' +
      /* 1:1 thumb; + chip pinned to the top-right corner, hard-square */
      '.fp-xsell-thumb{position:relative;display:block;width:96px;height:96px;background:#EDE8E1 50% 0/cover no-repeat;' +
      'margin-bottom:6px;cursor:pointer;border:0;padding:0;}' +
      '.fp-xsell-plus{position:absolute;top:0;right:0;width:28px;height:28px;background:#FFFFFF;border:0;' +
      'border-left:0.5px solid #EDE8E1;border-bottom:0.5px solid #EDE8E1;cursor:pointer;color:#424245;' +
      'display:flex;align-items:center;justify-content:center;transition:background 150ms ease,color 150ms ease;padding:0;}' +
      '.fp-xsell-plus:hover{background:#424245;color:#FFFFFF;}' +
      '.fp-xsell-title{font-size:10.5px;font-weight:300;color:#424245;line-height:1.3;margin:0 0 3px;' +
      'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:27px;}' +
      '.fp-xsell-title a{color:inherit;text-decoration:none;}' +
      '.fp-xsell-var{font-size:9px;font-weight:300;color:#8C8279;margin:0 0 2px;white-space:nowrap;' +
      'overflow:hidden;text-overflow:ellipsis;}' +
      '.fp-xsell-price{font-size:11px;font-weight:400;color:#424245;margin:0;}' +
      '.fp-xsell-price .fp-xsell-unit{font-size:9px;font-weight:300;color:#8C8279;}' +
      /* --- length-picker sheet (absolute inside .cart-drawer__dialog) --- */
      '.fp-xsell-veil{position:absolute;inset:0;background:rgba(66,66,69,0.32);opacity:0;' +
      'pointer-events:none;transition:opacity 200ms ease;z-index:8;}' +
      '.fp-xsell-veil[data-open]{opacity:1;pointer-events:auto;}' +
      '.fp-xsell-sheet{position:absolute;left:0;right:0;bottom:0;background:#FFFFFF;border-top:0.5px solid #EDE8E1;' +
      'padding:16px var(--padding-lg,20px) 20px;transform:translateY(110%);' +
      'transition:transform 200ms cubic-bezier(0.4,0,0.2,1);z-index:9;' +
      'font-family:var(--font-body--family,"Inter",sans-serif);}' +
      '@media screen and (min-width:750px){.fp-xsell-sheet{padding-inline:var(--padding-2xl,28px);}}' +
      '.fp-xsell-sheet[data-open]{transform:translateY(0);}' +
      '.fp-xsell-sheet__top{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;}' +
      '.fp-xsell-sheet__eyebrow{font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:#8C8279;font-weight:400;}' +
      '.fp-xsell-sheet__close{background:none;border:0;cursor:pointer;color:#8C8279;padding:4px;display:flex;transition:color 150ms ease;}' +
      '.fp-xsell-sheet__close:hover{color:#424245;}' +
      /* Product block (founder QC 2026-07-13): 80px image with title, price
         and the qty stepper stacked beside it, filling the image height. */
      '.fp-xsell-sheet__product{display:flex;gap:12px;margin-bottom:12px;}' +
      '.fp-xsell-sheet__thumb{width:80px;height:80px;flex:none;background:#EDE8E1 50% 0/cover no-repeat;}' +
      '.fp-xsell-sheet__info{min-width:0;flex:1;display:flex;flex-direction:column;justify-content:space-between;min-height:80px;}' +
      '.fp-xsell-sheet__title{font-size:12.5px;font-weight:400;color:#424245;margin:0 0 2px;line-height:1.3;' +
      'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
      '.fp-xsell-sheet__unit{font-size:11.5px;font-weight:300;color:#8C8279;margin:0;}' +
      '.fp-xsell-sheet__qtyrow{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:6px;}' +
      /* PDP-style variant pills (By meter / Shirt · 1.6m / …) */
      '.fp-xsell-sheet__variants{display:flex;flex-wrap:wrap;gap:6px;margin:0 0 12px;}' +
      '.fp-xsell-sheet__pill{padding:8px 12px;background:#FFFFFF;border:0.5px solid #D4CCC2;cursor:pointer;' +
      'font-family:inherit;font-size:11px;font-weight:300;color:#424245;line-height:1;transition:border-color 150ms ease;}' +
      '.fp-xsell-sheet__pill:hover{border-color:#8C8279;}' +
      '.fp-xsell-sheet__pill[data-selected]{background:#424245;border-color:#424245;color:#FFFFFF;font-weight:400;}' +
      '.fp-xsell-sheet__stepper{display:inline-flex;align-items:center;border:0.5px solid #D4CCC2;}' +
      '.fp-xsell-sheet__step{width:32px;height:32px;background:none;border:0;cursor:pointer;color:#424245;' +
      'display:flex;align-items:center;justify-content:center;transition:background 150ms ease;padding:0;}' +
      '.fp-xsell-sheet__step:hover{background:#EDE8E1;}' +
      '.fp-xsell-sheet__val{min-width:56px;text-align:center;font-size:13px;color:#424245;}' +
      '.fp-xsell-sheet__val small{font-size:11px;font-weight:300;color:#8C8279;}' +
      '.fp-xsell-sheet__calc{font-size:11px;font-weight:300;color:#8C8279;}' +
      '.fp-xsell-sheet__add{display:block;width:100%;padding:14px 0;background:#424245;color:#FFFFFF;border:0;' +
      'font-size:11px;letter-spacing:2.5px;text-transform:uppercase;font-weight:400;cursor:pointer;' +
      'font-family:inherit;transition:background 150ms ease;}' +
      '.fp-xsell-sheet__add:hover{background:#5C5A58;}' +
      '.fp-xsell-sheet__add[disabled]{opacity:0.5;cursor:default;}';
    document.head.appendChild(s);
  }

  // --- cart state -------------------------------------------------------------

  function readCart(cb) {
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(cb)
      .catch(function (e) { log('cart.js failed', e); });
  }

  // --- recommendations ---------------------------------------------------------

  // sessionStorage cache (30 min TTL): recommendations rarely change within a
  // session, and the cache is what makes the row render INSTANTLY instead of
  // waiting on a network round-trip after every add.
  var RECO_TTL_MS = 30 * 60 * 1000;
  function recoCacheGet(productId) {
    try {
      var raw = sessionStorage.getItem('fp_xs_' + productId);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.t || (Date.now() - obj.t) > RECO_TTL_MS) return null;
      return obj.p || null;
    } catch (e) { return null; }
  }
  function recoCachePut(productId, products) {
    try {
      // keep only the fields we use — sessionStorage space is shared
      var slim = (products || []).slice(0, 10).map(function (p) {
        return { id: p.id, title: p.title, url: p.url, available: p.available, featured_image: p.featured_image, tags: p.tags, variants: (p.variants || []).map(function (v) { return { id: v.id, title: v.title, available: v.available, price: v.price }; }) };
      });
      sessionStorage.setItem('fp_xs_' + productId, JSON.stringify({ t: Date.now(), p: slim }));
    } catch (e) {}
  }

  function fetchRecos(productId, cb) {
    var cached = recoCacheGet(productId);
    if (cached) { log('recos cache hit', productId); cb(cached, true); return; }
    fetch('/recommendations/products.js?product_id=' + productId + '&limit=8&intent=related', {
      headers: { 'Accept': 'application/json' },
    })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (res) {
        var products = (res && res.products) || [];
        recoCachePut(productId, products);
        cb(products, false);
      })
      .catch(function (e) { log('recos failed', e); cb([], false); });
  }

  // Pick the sellable variant: By-meter for fabrics, else first available.
  function chooseVariant(p) {
    var variants = p.variants || [];
    var byMeter = null, firstAvail = null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!firstAvail && v.available && String(v.title).indexOf('Cut ·') === -1) firstAvail = v;
      if (v.title === 'By meter') byMeter = v;
    }
    if (byMeter && byMeter.available) return { v: byMeter, unit: '/mtr', label: '' };
    if (firstAvail) {
      var perMtr = (p.tags || []).indexOf('per-mtr') !== -1;
      var label = firstAvail.title === 'Default Title' ? '' : String(firstAvail.title);
      return { v: firstAvail, unit: perMtr ? '/pc' : '', label: label };
    }
    return null;
  }

  function filterRecos(products) {
    var out = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (!p || !p.available) continue;
      if (state.inCartIds[p.id]) continue;
      if (/cut piece/i.test(p.title || '')) continue;
      var pick = chooseVariant(p);
      if (!pick) continue;
      // Sellable variant list for the sheet's PDP-style pills (founder QC
      // 2026-07-13): available, no cut-piece internals, no Default Title.
      var sellable = (p.variants || []).filter(function (v) {
        return v.available && String(v.title).indexOf('Cut ·') === -1 && v.title !== 'Default Title';
      });
      out.push({
        id: p.id, title: p.title, url: p.url, image: p.featured_image,
        variantId: pick.v.id, price: pick.v.price, unit: pick.unit, variantLabel: pick.label,
        variants: sellable,
        perMtr: (p.tags || []).indexOf('per-mtr') !== -1,
      });
      if (out.length >= MAX_CARDS + 2) break;
    }
    return out;
  }

  // --- length-picker sheet ------------------------------------------------------

  function sheetHost() {
    // Inside the <dialog> so the sheet renders in the top layer with it.
    return document.querySelector('.cart-drawer__dialog');
  }

  function ensureSheet() {
    var host = sheetHost();
    if (!host) return null;
    var sheet = host.querySelector('.fp-xsell-sheet');
    if (sheet) return sheet;

    var veil = document.createElement('div');
    veil.className = 'fp-xsell-veil';
    veil.addEventListener('click', closeSheet);

    sheet = document.createElement('div');
    sheet.className = 'fp-xsell-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-label', 'Select length');
    sheet.innerHTML =
      '<div class="fp-xsell-sheet__top">' +
      '<span class="fp-xsell-sheet__eyebrow">Select length</span>' +
      '<button type="button" class="fp-xsell-sheet__close" aria-label="Close">' +
      '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><path d="M3 3l10 10M13 3L3 13"/></svg>' +
      '</button></div>' +
      '<div class="fp-xsell-sheet__product">' +
      '<div class="fp-xsell-sheet__thumb"></div>' +
      '<div class="fp-xsell-sheet__info">' +
      '<div>' +
      '<p class="fp-xsell-sheet__title"></p>' +
      '<p class="fp-xsell-sheet__unit"></p>' +
      '</div>' +
      '<div class="fp-xsell-sheet__qtyrow">' +
      '<div class="fp-xsell-sheet__stepper">' +
      '<button type="button" class="fp-xsell-sheet__step" data-fp-step="-1" aria-label="Decrease">' +
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><path d="M2 6h8"/></svg>' +
      '</button>' +
      '<span class="fp-xsell-sheet__val" aria-live="polite"></span>' +
      '<button type="button" class="fp-xsell-sheet__step" data-fp-step="1" aria-label="Increase">' +
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><path d="M2 6h8M6 2v8"/></svg>' +
      '</button></div>' +
      '<span class="fp-xsell-sheet__calc"></span>' +
      '</div>' +
      '</div></div>' +
      '<div class="fp-xsell-sheet__variants" role="radiogroup" aria-label="Select length" hidden></div>' +
      '<button type="button" class="fp-xsell-sheet__add"></button>';

    sheet.querySelector('.fp-xsell-sheet__close').addEventListener('click', closeSheet);
    sheet.querySelectorAll('[data-fp-step]').forEach(function (b) {
      b.addEventListener('click', function () {
        var next = state.sheet.qty + Number(b.getAttribute('data-fp-step'));
        state.sheet.qty = Math.min(QTY_MAX, Math.max(1, next));
        paintSheet();
      });
    });
    // Variant pill selection (delegated — pills persist per product)
    sheet.querySelector('.fp-xsell-sheet__variants').addEventListener('click', function (e) {
      var pill = e.target && e.target.closest ? e.target.closest('.fp-xsell-sheet__pill') : null;
      var it = state.sheet.item;
      if (!pill || !it) return;
      var vid = Number(pill.getAttribute('data-fp-vid'));
      var match = (it.variants || []).filter(function (v) { return v.id === vid; })[0];
      if (!match) return;
      // Only a real change is a "consideration" signal — re-tapping the current
      // pill must not double-count. Compare against the EFFECTIVE selection
      // (sheetVariant() resolves the default pick when nothing's been tapped
      // yet), else the first tap on the pre-selected default would false-fire.
      var prevSel = sheetVariant();
      var changed = !prevSel || prevSel.id !== match.id;
      state.sheet.variant = match;
      if (changed) track('fp_xsell_variant_select', { fp_product: it.id, fp_variant: String(match.id), fp_value: (match.price * state.sheet.qty) / 100 });
      paintSheet();
    });
    // The sheet is a sub-modal INSIDE the cart drawer's <dialog>. The drawer
    // closes on any click it deems "outside" via isClickedOutside →
    // dialog.contains(event.target). A click on a control that gets removed
    // mid-handler (the old pill rebuild) left a detached target → read as
    // outside → drawer closed. Stop every sheet-internal click from ever
    // reaching that guard; the veil (a sibling, not inside the sheet) still
    // closes the sheet, and real outside clicks are unaffected.
    // (Founder QC 2026-07-13.)
    sheet.addEventListener('click', function (e) { e.stopPropagation(); });
    sheet.querySelector('.fp-xsell-sheet__add').addEventListener('click', function () {
      if (state.sheet.item) sheetAdd(this);
    });

    host.appendChild(veil);
    host.appendChild(sheet);
    return sheet;
  }

  // The variant the sheet is currently selling: the tapped pill, else the
  // card's default pick, else a synthetic fallback for single-variant products
  // (the lone "Default Title" SKU is filtered out of `variants`, so no match).
  function sheetVariant() {
    var it = state.sheet.item;
    if (!it) return null;
    if (state.sheet.variant) return state.sheet.variant;
    var match = (it.variants || []).filter(function (v) { return v.id === it.variantId; })[0];
    // Synthetic: carry the card's authoritative unit (`it.unit`, computed by
    // chooseVariant) instead of a fake title. Defaulting the title to
    // "By meter" mislabeled per-piece products (sarees/dupattas, whose only
    // variant is "Default Title" so variantLabel is '') as sold /mtr in the
    // sheet — right id/price, wrong unit+mts label. (Founder QC 2026-07-13.)
    return match || { id: it.variantId, title: it.variantLabel || '', price: it.price, unit: it.unit };
  }

  // /mtr for By-meter, /pc for pre-cut pieces of per-mtr fabrics, '' otherwise —
  // same suffix rules as price.liquid. The synthetic default already knows its
  // unit; trust it rather than re-deriving from a possibly-empty title.
  function variantUnit(it, v) {
    if (typeof v.unit === 'string') return v.unit; // synthetic default carries it
    if (v.title === 'By meter') return '/mtr';
    return it.perMtr ? '/pc' : '';
  }

  function paintSheet() {
    var host = sheetHost();
    if (!host) return;
    var sheet = host.querySelector('.fp-xsell-sheet');
    var it = state.sheet.item;
    if (!sheet || !it) return;
    var v = sheetVariant();
    var unit = variantUnit(it, v);
    var isMtr = unit === '/mtr'; // derive from unit, not title (synthetic titles can be empty)
    sheet.querySelector('.fp-xsell-sheet__thumb').style.backgroundImage =
      it.image ? 'url("' + imgUrl(it.image, 192).replace(/"/g, '%22') + '")' : '';
    sheet.querySelector('.fp-xsell-sheet__title').textContent = it.title;
    sheet.querySelector('.fp-xsell-sheet__unit').textContent = money(v.price) + (unit ? ' ' + unit : '');
    sheet.querySelector('.fp-xsell-sheet__val').innerHTML =
      state.sheet.qty + (isMtr ? ' <small>mts</small>' : '');
    sheet.querySelector('.fp-xsell-sheet__calc').textContent =
      state.sheet.qty + (isMtr ? ' mts × ' : ' × ') + money(v.price);

    // PDP-style variant pills — only when there's a real choice to make.
    // Build the pill nodes ONCE per product (keyed by data-for); on re-paint
    // only flip the selected marker. Rebuilding every paint detached the
    // just-clicked pill mid-event, which tripped the drawer's outside-click
    // guard and closed the whole drawer (founder QC 2026-07-13).
    var pillsHost = sheet.querySelector('.fp-xsell-sheet__variants');
    var vs = it.variants || [];
    if (vs.length > 1) {
      if (pillsHost.getAttribute('data-for') !== String(it.id)) {
        pillsHost.textContent = '';
        vs.forEach(function (opt) {
          var pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'fp-xsell-sheet__pill';
          pill.setAttribute('data-fp-vid', String(opt.id));
          pill.setAttribute('role', 'radio');
          pill.textContent = opt.title;
          pillsHost.appendChild(pill);
        });
        pillsHost.setAttribute('data-for', String(it.id));
      }
      pillsHost.querySelectorAll('.fp-xsell-sheet__pill').forEach(function (p) {
        var sel = Number(p.getAttribute('data-fp-vid')) === v.id;
        p.setAttribute('aria-checked', sel ? 'true' : 'false');
        if (sel) p.setAttribute('data-selected', ''); else p.removeAttribute('data-selected');
      });
      pillsHost.hidden = false;
    } else {
      pillsHost.textContent = '';
      pillsHost.removeAttribute('data-for');
      pillsHost.hidden = true;
    }

    var add = sheet.querySelector('.fp-xsell-sheet__add');
    add.disabled = false;
    add.textContent = 'Add to bag · ' + money(v.price * state.sheet.qty);
  }

  function openSheet(item) {
    ensureStyles();
    var sheet = ensureSheet();
    var host = sheetHost();
    if (!sheet || !host) return;
    state.sheet.item = item;
    state.sheet.qty = QTY_DEFAULT;
    state.sheet.variant = null; // reset to the card's default pick per open
    state.sheet.open = true;
    paintSheet();
    // force a frame so the transform transition runs on first open
    requestAnimationFrame(function () {
      host.querySelector('.fp-xsell-veil').setAttribute('data-open', '');
      sheet.setAttribute('data-open', '');
    });
    track('fp_xsell_open_picker', { fp_product: item.id });
  }

  function closeSheet() {
    var host = sheetHost();
    if (!host) return;
    state.sheet.open = false;
    var veil = host.querySelector('.fp-xsell-veil');
    var sheet = host.querySelector('.fp-xsell-sheet');
    if (veil) veil.removeAttribute('data-open');
    if (sheet) sheet.removeAttribute('data-open');
  }

  // --- render -------------------------------------------------------------------

  // Everything that affects a card's pixels. If unchanged between renders, the
  // card DOM is REUSED verbatim so its thumbnail never re-sets/reloads.
  function cardSig(it) {
    return [it.id, it.variantId, it.price, it.unit || '', it.variantLabel || '', it.image || '', it.title].join('|');
  }

  function buildCard(it) {
    var card = document.createElement('div');
    card.className = 'fp-xsell-card';
    card.setAttribute('data-fp-xs-id', String(it.id));
    card.setAttribute('data-fp-xs-sig', cardSig(it));

    // 1:1 thumb — button semantics; whole image opens the picker
    var thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = 'fp-xsell-thumb';
    thumb.setAttribute('aria-label', 'Choose length for ' + it.title);
    thumb.setAttribute('data-fp-xsell-open', String(it.id));
    if (it.image) thumb.style.backgroundImage = 'url("' + imgUrl(it.image, 192).replace(/"/g, '%22') + '")';

    var plus = document.createElement('span');
    plus.className = 'fp-xsell-plus';
    plus.setAttribute('aria-hidden', 'true');
    plus.innerHTML = '<svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><path d="M2 6h8M6 2v8"/></svg>';
    thumb.appendChild(plus);

    var t = document.createElement('p');
    t.className = 'fp-xsell-title';
    var tl = document.createElement('a');
    tl.href = it.url;
    tl.textContent = it.title;
    t.appendChild(tl);

    card.appendChild(thumb);
    card.appendChild(t);
    if (it.variantLabel) {
      var vl = document.createElement('p');
      vl.className = 'fp-xsell-var';
      vl.textContent = it.variantLabel;
      card.appendChild(vl);
    }
    var pr = document.createElement('p');
    pr.className = 'fp-xsell-price';
    pr.textContent = money(it.price);
    if (it.unit) {
      var u = document.createElement('span');
      u.className = 'fp-xsell-unit';
      u.textContent = ' ' + it.unit;
      pr.appendChild(u);
    }
    card.appendChild(pr);
    return card;
  }

  // KEYED-DIFF render (founder QC 2026-07-13): the old code cleared the anchor
  // and rebuilt every card each cart update — reloading all thumbnails even
  // when nothing changed. Now the eyebrow+row scaffold persists (paired with
  // data-skip-*-update on the anchor so the morph never wipes it either), and
  // only the cards that actually changed are (re)built; unchanged cards are
  // reused verbatim and just reordered if the filler sort moved them.
  function render() {
    var anchors = document.querySelectorAll('[data-fp-xsell]');
    if (!anchors.length) return;
    var items = state.products.slice();
    if (!items.length) { anchors.forEach(function (a) { a.hidden = true; }); return; }

    var remaining = state.nextMilestonePaise - state.cartTotal;
    if (remaining > 0 && remaining <= FILLER_WINDOW_PAISE) {
      items.sort(function (a, b) { return a.price - b.price; });
    }
    items = items.slice(0, MAX_CARDS);

    ensureStyles();
    anchors.forEach(function (anchor) {
      // Scaffold once; reused on every subsequent render.
      var row = anchor.querySelector('.fp-xsell-row');
      if (!row) {
        anchor.textContent = '';
        var eyebrow = document.createElement('p');
        eyebrow.className = 'fp-xsell-eyebrow';
        eyebrow.textContent = 'Customers also bought';
        row = document.createElement('div');
        row.className = 'fp-xsell-row';
        anchor.appendChild(eyebrow);
        anchor.appendChild(row);
      }

      // Index current cards by product id; reuse when the signature matches.
      var byId = {};
      [].slice.call(row.children).forEach(function (c) {
        var id = c.getAttribute && c.getAttribute('data-fp-xs-id');
        if (id) byId[id] = c;
      });
      var desired = items.map(function (it) {
        var cur = byId[String(it.id)];
        if (cur && cur.getAttribute('data-fp-xs-sig') === cardSig(it)) return cur; // reuse verbatim → no thumb reload
        return buildCard(it);
      });

      // Remove cards no longer wanted.
      [].slice.call(row.children).forEach(function (c) {
        if (desired.indexOf(c) === -1) row.removeChild(c);
      });
      // Place each desired node at its index (only moves what's out of order).
      desired.forEach(function (node, i) {
        if (row.children[i] !== node) row.insertBefore(node, row.children[i] || null);
      });
      anchor.hidden = false;
    });

    maybeTrackImpression(anchors, items.length);
  }

  function maybeTrackImpression(anchors, count) {
    if (state.impressionKey === state.cartKey) return;
    var visible = false;
    anchors.forEach(function (a) {
      if (a.hidden || !a.childElementCount) return;
      var dlg = a.closest('dialog');
      if (!dlg || dlg.open) visible = true;
    });
    if (!visible) return;
    state.impressionKey = state.cartKey;
    track('fp_xsell_impression', { fp_count: count || Math.min(state.products.length, MAX_CARDS) });
  }

  // --- add (silent-add pattern, now with quantity) --------------------------------

  function sheetAdd(btn) {
    var it = state.sheet.item;
    if (!it || btn.disabled) return;
    var v = sheetVariant(); // the pill the shopper actually selected
    if (!v) return;
    var qty = state.sheet.qty;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    var fd = new FormData();
    fd.set('id', String(v.id));
    fd.set('quantity', String(qty));
    if (sectionIds.length) fd.append('sections', Array.from(new Set(sectionIds)).join(','));

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
      body: fd,
    })
      .then(function (r) {
        return r.json().then(function (res) {
          if (!r.ok) throw new Error((res && (res.description || res.message)) || ('add failed ' + r.status));
          return res;
        });
      })
      .then(function (res) {
        // fp_value is the added line's revenue in RUPEES (v.price is paise).
        track('fp_xsell_add', { fp_product: it.id, fp_variant: String(v.id), fp_qty: qty, fp_value: (v.price * qty) / 100 });
        markUpsellCart(); // order-level attribution flag (non-blocking)
        closeSheet();
        // Bare CustomEvent (not CartAddEvent): drawer already open — morph only.
        var drawer = document.querySelector('cart-drawer-component');
        var hadAutoOpen = drawer && drawer.hasAttribute('auto-open');
        try {
          if (hadAutoOpen) drawer.removeAttribute('auto-open');
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: res, sourceId: 'fp-xsell', data: { source: 'fp-xsell', variantId: String(v.id), itemCount: qty, sections: res.sections } },
          }));
        } catch (e2) { log('post-add dispatch', e2); }
        finally { if (hadAutoOpen && drawer) drawer.setAttribute('auto-open', ''); }
      })
      .catch(function (err) {
        log('add failed', err);
        btn.disabled = false;
        btn.textContent = 'Retry';
      });
  }

  document.addEventListener('click', function (e) {
    var opener = e.target && e.target.closest ? e.target.closest('[data-fp-xsell-open]') : null;
    if (!opener) return;
    e.preventDefault();
    var pid = Number(opener.getAttribute('data-fp-xsell-open'));
    for (var i = 0; i < state.products.length; i++) {
      if (state.products[i].id === pid) { openSheet(state.products[i]); return; }
    }
  }, false);

  // Esc closes the picker before the dialog (capture phase beats the theme's handler)
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && state.sheet.open) { e.stopPropagation(); closeSheet(); }
  }, true);

  // --- refresh orchestration -------------------------------------------------------
  //
  // ZERO-FLICKER ARCHITECTURE (2026-07-12): every cart morph re-renders the
  // anchor EMPTY, and the old flow repainted it only after a 350ms debounce +
  // /cart.js + recommendations round-trips — the row visibly blinked on every
  // qty change and took >1s after adds. Now:
  //   1. A listener registered AT DOMContentLoaded runs AFTER the morph inside
  //      the SAME 'cart:update' dispatch (cart-items-component registers its
  //      morph listener at module-eval, i.e. earlier) → we update state from
  //      event.detail (no network) and repaint the anchor synchronously — the
  //      browser never paints the empty intermediate state.
  //   2. /cart.js is fetched ONLY when the event doesn't carry the full cart
  //      (adds carry just the item; qty updates carry the whole cart).
  //   3. Recommendations come from a sessionStorage cache (see fetchRecos) —
  //      and PDPs PREFETCH recos for their own product at idle, so the primary
  //      flow (ATC → drawer opens → row appears) is instant.

  // Update state from what the event already tells us. Returns 'full' when the
  // detail carried the whole cart, 'partial' when only the added item, or null.
  function applyDetail(detail) {
    var res = detail && detail.resource;
    if (!res) return null;
    if (res.items && typeof res.total_price === 'number') {
      // CartUpdateEvent: resource is the full cart json
      state.cartTotal = res.total_price || 0;
      state.inCartIds = {};
      var ids = [];
      res.items.forEach(function (it) { state.inCartIds[it.product_id] = true; ids.push(it.product_id); });
      state.cartKey = ids.join(',');
      if (res.items.length) state.sourceProductId = res.items[0].product_id;
      return res.items.length ? 'full' : 'empty';
    }
    if (res.product_id) {
      // CartAddEvent / silent adds: resource is the added item json
      state.inCartIds[res.product_id] = true;
      state.sourceProductId = res.product_id;
      state.cartKey = Object.keys(state.inCartIds).join(',');
      // total: read the freshly-morphed subtotal out of the drawer DOM
      var v = document.querySelector('.fp2-totals__value');
      if (v) {
        var n = parseInt((v.textContent || '').replace(/[^\d]/g, ''), 10);
        if (isFinite(n)) state.cartTotal = n * 100;
      }
      return 'partial';
    }
    return null;
  }

  function readMilestone() {
    var wrap = document.querySelector('.fp-freeship-wrapper');
    var nextR = wrap ? parseInt(wrap.getAttribute('data-next-milestone'), 10) : 0;
    state.nextMilestonePaise = (nextR || 0) * 100;
  }

  // Synchronous repaint from cached products (no network) — used right after
  // each morph. Products already in the cart filter out immediately.
  function renderFromCache() {
    if (!state.products.length) return;
    state.products = state.products.filter(function (p) { return !state.inCartIds[p.id]; });
    readMilestone();
    render();
  }

  // Background reconcile: fetch whatever the event didn't carry, refetch recos
  // only when the newest product actually changed.
  function reconcile(kind) {
    var anchors = document.querySelectorAll('[data-fp-xsell]');
    if (!anchors.length) return;
    var proceed = function (newest) {
      var reqKey = state.cartKey, reqSource = newest;
      fetchRecos(newest, function (products) {
        if (state.cartKey !== reqKey || state.sourceProductId !== reqSource) { log('stale recos discarded'); return; }
        state.products = filterRecos(products);
        readMilestone();
        render();
      });
    };
    if (kind === 'full' || kind === 'partial') {
      if (state.recosFor !== state.sourceProductId) {
        state.recosFor = state.sourceProductId;
        proceed(state.sourceProductId);
      } else {
        // Same source product: re-filter from cache (no network) so items
        // REMOVED from the cart re-enter the row, and freshly added ones drop.
        var cached = recoCacheGet(state.sourceProductId);
        if (cached) {
          state.products = filterRecos(cached);
          readMilestone();
          render();
        }
      }
      return;
    }
    // Detail told us nothing — full read (first load, exotic dispatchers).
    readCart(function (cart) {
      var items = (cart && cart.items) || [];
      if (!items.length) { anchors.forEach(function (a) { a.hidden = true; }); closeSheet(); return; }
      state.cartTotal = cart.total_price || 0;
      state.inCartIds = {};
      var ids = [];
      items.forEach(function (it) { state.inCartIds[it.product_id] = true; ids.push(it.product_id); });
      state.cartKey = ids.join(',');
      state.sourceProductId = items[0].product_id;
      state.recosFor = state.sourceProductId;
      proceed(state.sourceProductId);
    });
  }

  function onCartUpdatePostMorph(event) {
    var kind = applyDetail(event && event.detail);
    if (kind === 'empty') {
      document.querySelectorAll('[data-fp-xsell]').forEach(function (a) { a.hidden = true; });
      closeSheet();
      return;
    }
    // Same-task repaint: the morphed (empty) anchor is refilled before the
    // browser gets a chance to paint.
    renderFromCache();
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () { reconcile(kind); }, 250);
  }

  // PDP prefetch: the newest cart item after an add IS this product — warm the
  // reco cache at idle so the drawer's row renders with zero wait.
  function prefetchPdpRecos() {
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      var pid = meta && meta.product && meta.product.id;
      if (!pid || recoCacheGet(pid)) return;
      var go = function () { fetchRecos(pid, function () { log('prefetched recos for', pid); }); };
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 4000 });
      else setTimeout(go, 2500);
    } catch (e) {}
  }

  // Re-fill wiped anchors after the header hydration morph (fires no
  // cart:update) and close the sheet when the dialog closes. The sheet+veil
  // live directly on .cart-drawer__dialog, OUTSIDE the morphed inner, so they
  // survive morphs.
  var domTick = null;
  function onDomChange() {
    var anchors = document.querySelectorAll('[data-fp-xsell]');
    var dlg = document.querySelector('.cart-drawer__dialog');
    if (dlg && !dlg.open && state.sheet.open) closeSheet();
    if (!anchors.length || !state.products.length) return;
    var wiped = false;
    anchors.forEach(function (a) { if (!a.childElementCount) wiped = true; });
    if (wiped) render();
    else maybeTrackImpression(anchors, 0);
  }
  function installObserver() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    new MutationObserver(function () {
      if (domTick) clearTimeout(domTick);
      domTick = setTimeout(onDomChange, 150);
    }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  }

  // Registration AT DCL is what guarantees post-morph ordering (see header
  // note). This script is `defer`, so it always runs before DOMContentLoaded.
  function boot() {
    document.addEventListener('cart:update', onCartUpdatePostMorph, false);
    document.addEventListener('discount:update', function () {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(function () { reconcile(null); }, 250);
    }, false);
    installObserver();
    prefetchPdpRecos();
    setTimeout(function () { reconcile(null); }, 300);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  log('installed v2.1');
})();
